// Shared, single-flight, TTL-cached read of the Location's canonical GBP
// endpoint (GET /api/b/:businessId/l/:locationId/gbp) — the ONE
// endpoint feeding four surfaces (overview's signal feed, the Google Profile
// screen's profile + content tabs, Task 9's reviews screen). The endpoint
// triggers a real Google sync server-side, so fetching it once per TTL
// window regardless of how many components are mounted at once is the whole
// point of this file (WP1 Task 8 — see the brief's "riskiest migration in
// WP1" note).
//
// Subscriber-pattern precedent: hooks/use-me.ts. Two differences from that
// precedent, both deliberate:
//   1. Keyed by the Business+Location pair (a Map of caches/subscriber-sets,
//      not one global singleton) — this screen is per-Location, use-me.ts's
//      /api/me is not.
//   2. A real in-flight promise map, not just a `cache ? skip : fetch`
//      check. use-me.ts's own "if (!cache) refresh()" on every mount is NOT
//      single-flight-safe: two components mounting in the same commit would
//      both see `cache === null` and both call refresh(), firing two
//      requests. That race is exactly what this task's brief requires this
//      hook NOT have (single-flight is pinned by a dedicated test), so it
//      is not copied verbatim.
import { useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { TERMS } from '@pageboss/shared';
import { api } from '@/lib/api';
import type { GbpResponse } from '@/lib/types';

export type GbpDataState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: GbpResponse }
  | { kind: 'error'; message: string };

// Real Google sync server-side (Phase 7 S1+S2) — 60s keeps every consumer
// honest about staleness while still collapsing bursts of mounts (e.g.
// overview → click into the Google Profile screen) into one network call.
const TTL_MS = 60_000;

interface CacheEntry {
  data: GbpResponse;
  fetchedAt: number;
}

// T1.6B2: every map below is keyed by BOTH tenant identifiers — the cache
// key is `${businessId}/${locationId}`, never a bare location id. A Location
// belongs to exactly one Business, so a bare location key was not wrong by
// arithmetic; it was wrong by contract — it let a cache entry written under
// one canonical route be read by a component whose route named a different
// Business, and it made the "which tenant is this?" question unanswerable at
// the point of the read. Both ids come from the canonical route and are
// passed in explicitly; nothing here infers one from the other.
function tenantKey(businessId: string, locationId: string): string {
  return `${businessId}/${locationId}`;
}

const cache = new Map<string, CacheEntry>();
const subscribers = new Map<string, Set<(state: GbpDataState) => void>>();
// The single-flight guard: a tenant key maps to its in-progress request only
// while one is outstanding. A second, third, ... consumer mounting (or
// re-checking staleness) while an entry is present here reuses the SAME
// promise instead of firing a second `fetch` — this is set synchronously,
// before the request's own microtask queue runs, so two effects that fire
// back-to-back in the same commit (React runs mount effects for siblings in
// the same flush) both observe the entry the first one set.
const inFlight = new Map<string, Promise<void>>();

function notify(key: string, state: GbpDataState): void {
  const subs = subscribers.get(key);
  if (!subs) return;
  for (const fn of subs) fn(state);
}

function isFresh(key: string): boolean {
  const entry = cache.get(key);
  return !!entry && Date.now() - entry.fetchedAt < TTL_MS;
}

// The normal, cacheable path. Single-flight via `inFlight`; updates `cache`
// and notifies every current subscriber for this tenant on settle. Both ids
// are the canonical route's, proven by the server on the request that
// rendered the caller; neither is ever inferred from the other.
function fetchGbp(businessId: string, locationId: string): Promise<void> {
  const key = tenantKey(businessId, locationId);
  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = api
    .get<GbpResponse>(locationApi(businessId, locationId, 'gbp'))
    .then((data) => {
      cache.set(key, { data, fetchedAt: Date.now() });
      notify(key, { kind: 'ready', data });
    })
    .catch((err: unknown) => {
      notify(key, {
        kind: 'error',
        message: err instanceof Error ? err.message : `Could not load your ${TERMS.googleProfile}.`,
      });
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
}

export interface UseGbpDataOptions {
  // Set ONLY by the Google Profile screen's `?select=` picker landing (see
  // `fetchGbpForSelect` below): suppresses this hook's own auto-fetch on
  // mount so it never races the caller's separate, non-cacheable
  // `?select=1` request for the same siteId — both would otherwise write
  // the same cache entry from two concurrent network calls, which is
  // exactly the double-fetch this hook exists to prevent. The caller is
  // responsible for populating the cache itself (via `fetchGbpForSelect`)
  // when this is set.
  skipAutoFetch?: boolean;
}

// CONSOLIDATION A6 (binding, WP1 Task 8 + Task 11): shared by the Google
// Profile screen's map tab and the visibility layer landing screen (Task
// 11) — both render a non-blocking "unverified — connect your Google
// Business Profile" notice under the exact same condition, extracted here
// so neither screen can drift from the other. 'locked' is a Starter-tier
// gate, not an ownership question — the locked panel (or the landing card's
// own lock marker) already tells that story with its own upgrade path, and
// showing "connect your Google profile" on top of it would contradict a
// customer who has never even been able to try connecting. The banner is
// only for the genuinely-actionable non-connected states.
export function needsUnverifiedBanner(gbp: GbpDataState): boolean {
  return (
    gbp.kind === 'ready' && (gbp.data.state === 'not_connected' || gbp.data.state === 'pending_selection')
  );
}

export function useGbpData(
  businessId: string,
  locationId: string,
  opts?: UseGbpDataOptions,
): GbpDataState {
  const skipAutoFetch = opts?.skipAutoFetch ?? false;
  // A Location read needs BOTH ids. Either one missing is not a cache miss to
  // paper over — it is "no Location in scope", and the honest answer is to
  // stay in `loading` and issue no request at all.
  const ready = Boolean(businessId) && Boolean(locationId);
  const key = ready ? tenantKey(businessId, locationId) : '';
  const [state, setState] = useState<GbpDataState>(() =>
    key && isFresh(key) ? { kind: 'ready', data: cache.get(key)!.data } : { kind: 'loading' },
  );

  useEffect(() => {
    if (!key) return;
    // Re-scope on EITHER id changing: `key` is in the dependency list, so
    // navigating B1/L1 → B1/L2 or B1/L1 → B2/L3 unsubscribes from the old
    // tenant and re-reads (or re-fetches) under the new one. A subscriber
    // never survives a tenant change.
    setState(isFresh(key) ? { kind: 'ready', data: cache.get(key)!.data } : { kind: 'loading' });

    let subs = subscribers.get(key);
    if (!subs) {
      subs = new Set();
      subscribers.set(key, subs);
    }
    subs.add(setState);

    if (!isFresh(key) && !skipAutoFetch) {
      void fetchGbp(businessId, locationId);
    }

    return () => {
      const current = subscribers.get(key);
      current?.delete(setState);
      if (current && current.size === 0) subscribers.delete(key);
    };
  }, [key, businessId, locationId, skipAutoFetch]);

  return state;
}

// Drops the cached entry and re-fetches immediately — "next consumer
// refetches" (brief's Interfaces line), plus every CURRENTLY mounted
// consumer for this siteId also picks up the fresh result once it lands
// (same as use-me.ts's invalidateMe(), which also re-fetches rather than
// merely clearing). Deliberately does NOT emit a synchronous 'loading'
// notification first — every mounted consumer keeps showing its last-known
// data until the fresh fetch resolves, so a background reconcile (e.g.
// after a write elsewhere on the GBP screen) never flashes a skeleton over
// content that is still correct for another moment.
export function invalidateGbp(businessId: string, locationId: string): void {
  if (!businessId || !locationId) return;
  cache.delete(tenantKey(businessId, locationId));
  void fetchGbp(businessId, locationId);
}

// One-shot, deliberately NOT single-flighted with the normal path: the
// picker-completing OAuth redirect (`?select=` on the Google Profile
// screen's URL) requires the query param on the wire to trigger the
// server's picker branch (routes/api/gbp.ts: `c.req.query('select') !==
// undefined`), which consumes and reissues a signed one-time cookie
// challenge — that request is inherently non-cacheable and must never be
// coalesced with a concurrent plain fetch for the same siteId. Callers:
// only the Google Profile screen, exactly once on mount (mirrors the
// pre-Task-8 site-gbp.tsx `[selectPending]` capture-once pattern) — calling
// this more than once per landing would burn the one-time challenge early.
export function fetchGbpForSelect(businessId: string, locationId: string): void {
  if (!businessId || !locationId) return;
  const key = tenantKey(businessId, locationId);
  notify(key, { kind: 'loading' });
  void api
    .get<GbpResponse>(locationApi(businessId, locationId, 'gbp?select=1'))
    .then((data) => {
      cache.set(key, { data, fetchedAt: Date.now() });
      notify(key, { kind: 'ready', data });
    })
    .catch((err: unknown) => {
      notify(key, {
        kind: 'error',
        message: err instanceof Error ? err.message : `Could not load your ${TERMS.googleProfile}.`,
      });
    });
}

// Optimistic local write: a DELETE's 204 (disconnect) already proves the
// connection + stored reviews are gone server-side, so the screen can show
// `not_connected` on that alone rather than wait on a refetch (pre-Task-8
// site-gbp.tsx's `handleDisconnected` comment: "Disconnect is the one
// success that must NOT wait on a refetch"). Updates the shared cache (so
// every OTHER mounted consumer — e.g. overview, if it happens to be
// prefetched — also flips instantly) and schedules a background reconcile.
export function setGbpDisconnected(businessId: string, locationId: string): void {
  if (!businessId || !locationId) return;
  const key = tenantKey(businessId, locationId);
  const data: GbpResponse = { state: 'not_connected' };
  cache.set(key, { data, fetchedAt: Date.now() });
  notify(key, { kind: 'ready', data });
  invalidateGbp(businessId, locationId);
}

// Test-only: this module's cache/inFlight state is a real module-level
// singleton (by design — that's what makes it single-flight/shared across
// components), which means it persists across `test()` blocks that reuse
// the same tenant pair within one file (e.g. hooks/__tests__/use-overview-
// signals.test.tsx, whose fixtures predate this hook and all use `site1`).
// Call this in an `afterEach`/`beforeEach` in any test file that reuses a
// tenant pair across tests with different stubbed responses.
export function resetGbpCacheForTests(): void {
  cache.clear();
  subscribers.clear();
  inFlight.clear();
}
